import { AuthEntryTabs, AuthIdentifierTabs } from './AuthEntryTabs';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, ChevronDown, Eye, EyeOff } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useAppStore } from '../../app/appStore';
import { e164Digits } from '../../shared/country/countryConfig';
import { useCountryStore } from '../../shared/country/countryStore';
import { phonePlan } from '../../shared/country/phonePlan';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { register } from './api';
import { GoogleSignIn } from './GoogleSignIn';
import { useAuthStore } from './authStore';
import { CountryDetectedHint, CountryPicker } from './country/CountryPicker';
import { RegisterCountryNotice } from './RegisterCountryNotice';
import { RegistrationChallenge } from './RegistrationChallenge';
import {
  AuthLangBar,
  BrandMini,
  ErrLine,
  HintLine,
  LegalFooter,
  authErrorMessage,
  identifierInputMode,
  phoneHintText,
  scrollAuthInputIntoView,
  writeAuthPrefs,
  AuthLegalFoot,
} from './authShared';

/** Password registration; phone ownership is verified separately. */
export function RegisterForm({
  onAuthenticated,
  initialIdentifier,
  onIdentifierChange,
  remember,
}: {
  onAuthenticated: () => void;
  initialIdentifier: string;
  onIdentifierChange: (value: string) => void;
  remember: boolean;
}) {
  const setRoute = useAppStore((state) => state.setRoute);
  const showToast = useAppStore((state) => state.showToast);
  const t = useI18nStore((state) => state.t);
  const country = useCountryStore((state) => state.country);
  const marketAvailable = useCountryStore(state => state.loaded && state.countries.length > 0);
  const setSession = useAuthStore((state) => state.setSession);

  const [identifierMode, setIdentifierMode] = useState<'phone' | 'email'>(initialIdentifier.includes('@') ? 'email' : 'phone');
  const [email, setEmail] = useState(initialIdentifier.includes('@') ? initialIdentifier : '');
  const [phone, setPhone] = useState(() => (
    identifierInputMode(initialIdentifier) === 'phone' ? phonePlan(country).format(initialIdentifier, country) : ''
  ));
  const [phoneFocused, setPhoneFocused] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [verificationAttempt, setVerificationAttempt] = useState(0);
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(true);
  const [acceptedMarketing, setAcceptedMarketing] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [inviteExpanded, setInviteExpanded] = useState(false);



  // Mirrors useAuthIdentifier's own onChange effect (Register does not use
  // that hook — see the module comment above — but still owes the shell the
  // same draft updates so navigating away carries the latest value).
  const onIdentifierChangeRef = useRef(onIdentifierChange);
  onIdentifierChangeRef.current = onIdentifierChange;
  useEffect(() => {
    onIdentifierChangeRef.current(identifierMode === 'email' ? email : phone);
  }, [phone, email, identifierMode]);

  const lastCountryCodeRef = useRef(country.code);
  useEffect(() => {
    if (lastCountryCodeRef.current === country.code) return;
    lastCountryCodeRef.current = country.code;
    setPhone((current) => phonePlan(country).format(current, country));
  }, [country]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Legacy accepts ?invite= or ?ref=, in the search string or embedded in the hash.
    const search = new URLSearchParams(window.location.search);
    let invite = search.get('invite') || search.get('ref') || '';
    if (!invite && window.location.hash.includes('?')) {
      const hashQuery = new URLSearchParams(window.location.hash.slice(window.location.hash.indexOf('?') + 1));
      invite = hashQuery.get('invite') || hashQuery.get('ref') || '';
    }
    if (invite) setInviteCode(invite.slice(0, 16));
  }, []);

  const mutation = useMutation({
    mutationFn: () => register({ ...(identifierMode === 'email' ? { email } : { phone }), confirmPassword, turnstileToken, password, inviteCode, acceptedTerms, acceptedMarketing }),
    onError: () => { setTurnstileToken(''); setVerificationAttempt(v => v + 1); },
    onMutate: () => {
      showToast(t('authCreatingAccount'), 'info');
    },
    onSuccess: (data) => {
      writeAuthPrefs(identifierMode === 'email' ? email.trim() : phone, remember);
      // Legacy always routes register into username setup regardless of backend flag.
      const nextUser = data.user ? { ...data.user, requires_username_setup: true } : data.user;
      setSession({
        token: data.token,
        expiresAt: Number(data.expires_at ?? data.expiresAt ?? 0),
        sessionSecret: data.sessionSecret,
        user: nextUser,
        remember,
      });
      showToast(t('authAccountCreated'), 'success');
      onAuthenticated();
    },
  });

  // Mobile phone numbers remain the registration identifier; a Brazilian
  // landline — which isValid accepts for login/forgot's sake — must be
  // rejected here before it ever reaches the backend's BR_PHONE check.
  const phoneValid = !!phone.trim() && phonePlan(country).isValid(phone, country) && phonePlan(country).isMobile(phone, country);
  // { mobileOnly: true } — this screen's submit gate is isMobile, not isValid
  // (see the comment on `phoneValid` above), so a landline needs its own hint
  // here that login/forgot (which share the same `hint()` via useAuthIdentifier)
  // must not show, since a landline is a legitimate pre-existing login.
  const phoneHint = phone.trim() ? phoneHintText(phone, country, t, { mobileOnly: true }) : '';
  const passwordInvalid = !!password && !/^(?=.*[A-Za-z])(?=.*\d).{6,32}$/.test(password);


  const e164 = phone.trim() ? e164Digits(phone, country) : '';
  const lastE164Ref = useRef(e164);
  useEffect(() => {
    if (lastE164Ref.current === e164) return;
    lastE164Ref.current = e164;
    mutation.reset();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [e164]);

  const emailValid = email.trim().length <= 254 && /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email.trim());
  const canSubmit = marketAvailable && (identifierMode === 'email' ? emailValid : phoneValid) && password === confirmPassword && !!turnstileToken && acceptedTerms && /^(?=.*[A-Za-z])(?=.*\d).{6,32}$/.test(password);
  const primaryDisabled = !canSubmit || mutation.isPending;

  function handlePhoneInput(value: string) {
    // Registration is phone-only, so this field does not go through the
    // login page's email / username detection: anything that is not a digit
    // is dropped and the country mask applies from the first digit. The plan
    // also strips a pasted calling code and caps at the national length.
    const plan = phonePlan(country);
    const prev = phone;
    const prevDigits = plan.normalize(prev, country);
    const nextDigits = plan.normalize(value, country);
    let nextValue: string;
    if (value.length < prev.length && nextDigits === prevDigits && prevDigits.length > 0) {
      // Backspace on a mask character (digit count unchanged) removes the
      // last digit instead of re-producing the same mask.
      nextValue = plan.format(prevDigits.slice(0, -1), country);
    } else {
      nextValue = plan.format(nextDigits, country);
    }
    setPhone(nextValue);
    // Clear any previous submission error when the identifier changes.
    mutation.reset();
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (canSubmit && !mutation.isPending) mutation.mutate();
  }

  return (
    <>
      <div className="px-4 pt-4 pb-1 flex items-center justify-between relative z-[1]">
        <button type="button" className="text-white/80 text-sm inline-flex items-center gap-1 tap" onClick={() => setRoute('auth.login')}><ArrowLeft size={16} aria-hidden="true" /><span>{t('backToLogin')}</span></button>
        <BrandMini />
      </div>
      <div className="flex-1 flex flex-col justify-start px-6 pt-8 pb-4 relative z-[1]">
        <div className="text-center mb-5">
          <h1 className="text-ink-1 text-xl font-bold">{t('createAccount')}</h1>
          <p className="text-[12.5px] mt-1.5" style={{ color: 'var(--ds-ink-3)' }}>{t('registerSub')}</p>
        </div>
        <form className="auth-card" onSubmit={submit} onFocusCapture={scrollAuthInputIntoView}>
          <AuthEntryTabs mode="register" onChange={() => setRoute('auth.login')} />
          <AuthIdentifierTabs mode={identifierMode} onChange={mode => { setIdentifierMode(mode); mutation.reset(); }} />
          {identifierMode === 'email' ? <div className="auth-phone-field auth-email-field">
            <CountryPicker showPhonePrefix={false} />
            <input className="auth-input" type="email" autoComplete="email" inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} maxLength={254} value={email} onChange={event => { setEmail(event.target.value); mutation.reset(); }} placeholder={t('registerByEmail')} aria-label={t('registerByEmail')} />
          </div> : <>
          <div className={`auth-phone-field${phone.trim() && !phoneValid ? ' is-error' : ''}`}>
            <CountryPicker />
            <input
              className="auth-input auth-phone"
              autoComplete="tel"
              inputMode="tel"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              maxLength={20}
              value={phone}
              onChange={(event) => handlePhoneInput(event.target.value)}
              onFocus={() => setPhoneFocused(true)}
              onBlur={() => setPhoneFocused(false)}
              placeholder={t('placeholderPhone')}
            />
          </div>
          <CountryDetectedHint />
          {phoneFocused && !phone.trim() ? <HintLine text={t('phoneTip')} /> : null}
          {phoneHint ? <HintLine tone="amber" text={phoneHint} /> : null}
          </>}

          <div className="auth-input-wrap mt-2">
            <input className="auth-input pr-11" autoComplete="new-password" type={showPassword ? 'text' : 'password'} value={password} onChange={(event) => setPassword(event.target.value)} placeholder={t('placeholderPwd')} />
            <button type="button" className="auth-input-eye tap" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? t('hidePwd') : t('showPassword')}>{showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}</button>
          </div>
          <p className={`text-[11px] mt-1 ml-1 ${passwordInvalid ? 'text-rose-400' : 'text-white/40'}`}>{t('passwordRuleHint')}</p>

          <div className="auth-input-wrap mt-2">
            <input className="auth-input pr-11" autoComplete="new-password" type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={e=>setConfirmPassword(e.target.value)} placeholder={t('confirmPasswordLabel')} aria-label={t('confirmPasswordLabel')} />
          </div>
          {confirmPassword && password !== confirmPassword ? <ErrLine text={t('passwordsMismatch')} /> : null}

          <button type="button" className="auth-invite-toggle mt-3 w-full inline-flex items-center justify-between text-[12px] text-white/70 tap" onClick={() => setInviteExpanded((value) => !value)}>
            <span>{t('inviteCode')}</span>
            <ChevronDown size={14} className={`transition-transform${inviteExpanded ? ' rotate-180' : ''}`} aria-hidden="true" />
          </button>
          {inviteExpanded ? <input className="auth-input mt-2" autoComplete="off" autoCapitalize="characters" value={inviteCode} onChange={(event) => setInviteCode(event.target.value.slice(0, 16))} placeholder={t('invitePlaceholder')} /> : null}
          <label className="flex items-start gap-2 mt-3 cursor-pointer select-none">
            <input type="checkbox" className="auth-checkbox mt-0.5" checked={acceptedTerms} onChange={(event) => setAcceptedTerms(event.target.checked)} />
            <span className="text-[12px] text-ink-2 leading-snug">{t('agreeTermsLabelPrefix')}<a href="/termos.html" target="_blank" rel="noopener" className="text-gold-400 underline" onClick={(event) => event.stopPropagation()}>{t('agreeTermsLink')}</a></span>
          </label>
          <label className="flex items-start gap-2 mt-2 cursor-pointer select-none">
            <input type="checkbox" className="auth-checkbox mt-0.5" checked={acceptedMarketing} onChange={(event) => setAcceptedMarketing(event.target.checked)} />
            <span className="text-[12px] text-ink-2 leading-snug">{t('marketingOptIn')}</span>
          </label>

          {mutation.isError ? <p className="text-xs text-rose-400 mt-2">{authErrorMessage(mutation.error, t, identifierMode, 'authError')}</p> : null}

          {/* Immediately above submit: the submit button is the confirmation,
              so the country has to be the last thing read before the tap. */}
          {marketAvailable ? <RegisterCountryNotice /> : <p className="text-rose-400 text-xs">{t('errCountryNotSupported')}</p>}
          <RegistrationChallenge onToken={setTurnstileToken} attempt={verificationAttempt} />

          <button className="btn-gold-primary w-full py-3 rounded-xl mt-3 transition-all tap" type="submit" disabled={primaryDisabled}>
            {t('createAccountAction')}
          </button>
          <GoogleSignIn onAuthenticated={onAuthenticated} remember={remember} registration={{ turnstileToken, acceptedTerms, acceptedMarketing, inviteCode, onAttempt: () => { setTurnstileToken(''); setVerificationAttempt(v => v + 1); } }} />
        </form>
        <LegalFooter t={t} showResponsible={false} />
        {/* The login screen has always had this; register did not, and register
            is exactly where an invited player lands — `invite_link` points at
            `/#/auth.register?invite=…`. Without it, someone arriving from a
            friend's link is stuck in whatever language the domain defaults to. */}
        <div className="flex justify-center mt-2"><AuthLangBar /></div>
        <div className="mt-2"><AuthLegalFoot /></div>
      </div>
    </>
  );
}
